import React, { useState } from 'react'

import {
  Box,
  Button,
  Checkbox,
  Chip,
  IconButton,
  Menu,
  MenuItem,
  TablePagination,
  TableRow,
  Card,
} from '@material-ui/core'
import {
  Delete as DeleteIcon,
  MoreVert as MoreVertIcon,
  NotificationsNone as NotifyIcon,
  PersonAdd as PersonAddIcon,
} from '@material-ui/icons'
import { isEmpty } from 'lodash'

import { FilterButton, LoadingFeedback, Filters } from 'components'
import { Table, TableBody, TableCell, TableHead } from 'components/Table'
import useFetch from 'hooks/useFetch'
import useFilter from 'hooks/useFilter'
import usePagination from 'hooks/usePagination'
import useSnackbar from 'hooks/useSnackbar'
import * as service from 'service'

import {
  ConfirmDeleteDialog,
  ConfirmInactivateDialog,
  ConfirmMassDeleteDialog,
  EnrollmentFormDialog,
  LinkStudentDialog,
  NotifyDialog,
} from './components'
import useStyles from './styles'

import helpers from 'helpers'
import PublicLinkBanner from 'views/TrainingCourses/components/PublicLinkBanner'

const STATUS_LABELS = { active: 'Ativo', inactive: 'Inativo' }
const STATUS_COLORS = { active: 'primary', inactive: 'default' }

const RowMenu = ({ enrollment, onEdit, onInactivate, onDelete, onNotify }) => {
  const [anchorEl, setAnchorEl] = useState(null)

  const handleOpen = (event) => setAnchorEl(event.currentTarget)
  const handleClose = () => setAnchorEl(null)

  const action = (callback) => () => {
    handleClose()
    callback(enrollment)
  }

  return (
    <>
      <IconButton size="small" onClick={handleOpen}>
        <MoreVertIcon fontSize="small" />
      </IconButton>
      <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleClose}>
        <MenuItem onClick={action(onNotify)}>Notificar</MenuItem>
        <MenuItem onClick={action(onEdit)}>Editar</MenuItem>
        <MenuItem onClick={action(onInactivate)}>
          {enrollment.status === 'active' ? 'Inativar' : 'Ativar'}
        </MenuItem>
        <MenuItem onClick={action(onDelete)}>Excluir</MenuItem>
      </Menu>
    </>
  )
}

const StepManagePeople = ({ courseId, isEdit }) => {
  const classes = useStyles()
  const snackbar = useSnackbar()
  const filter = useFilter()
  const { page, perPage, handleChangePage, handleChangeRowsPerPage } =
    usePagination(10)

  const [selected, setSelected] = useState([])
  const [editEnrollment, setEditEnrollment] = useState(null)
  const [deleteEnrollment, setDeleteEnrollment] = useState(null)
  const [inactivateEnrollment, setInactivateEnrollment] = useState(null)
  const [notifyEnrollment, setNotifyEnrollment] = useState(null)
  const [linkOpen, setLinkOpen] = useState(false)
  const [massNotifyOpen, setMassNotifyOpen] = useState(false)
  const [massDeleteOpen, setMassDeleteOpen] = useState(false)
  const [massDeleteLoading, setMassDeleteLoading] = useState(false)

  const { response, isLoading, refresh } = useFetch(
    service.dponet.trainingEnrollment.index,
    { courseId, page, perPage, ...filter.filters },
    [courseId, page, perPage, filter.filters],
  )

  const enrollments = response?.data?.enrollments ?? []
  const totalCount = response?.data?.meta?.totalCount ?? 0

  const formatteDate = (date, have_hours = false) => {
    let format = have_hours ? 'DD/MM/YYYY HH:mm' : 'DD/MM/YYYY'
    return helpers.formatters.date(date, format)
  }

  const allSelected =
    !isEmpty(enrollments) && selected.length === enrollments.length
  const someSelected = selected.length > 0 && !allSelected

  const toggleSelectAll = () =>
    setSelected(
      allSelected ? [] : enrollments.map((enrollment) => enrollment.id),
    )

  const toggleSelect = (id) =>
    setSelected((prev) =>
      prev.includes(id)
        ? prev.filter((selected) => selected !== id)
        : [...prev, id],
    )

  const handleDeleteSuccess = () => {
    setSelected((prev) => prev.filter((id) => id !== deleteEnrollment?.id))
    setDeleteEnrollment(null)
    refresh()
    snackbar.open({
      message: 'Aluno removido com sucesso.',
      variant: 'success',
    })
  }

  const handleInactivateSuccess = () => {
    setInactivateEnrollment(null)
    refresh()
    snackbar.open({
      message: 'Status do aluno atualizado com sucesso.',
      variant: 'success',
    })
  }

  const handleEditSuccess = () => {
    setEditEnrollment(null)
    refresh()
    snackbar.open({
      message: 'Aluno atualizado com sucesso.',
      variant: 'success',
    })
  }

  const handleLinkSuccess = () => {
    setLinkOpen(false)
    refresh()
    snackbar.open({
      message: 'Aluno vinculado com sucesso.',
      variant: 'success',
    })
  }

  const handleNotifySuccess = () => {
    setNotifyEnrollment(null)
    setMassNotifyOpen(false)
    refresh()
    snackbar.open({
      message: 'Notificação enviada com sucesso.',
      variant: 'success',
    })
  }

  const handleMassDelete = async () => {
    setMassDeleteLoading(true)
    try {
      await Promise.all(
        selected.map((enrollmentId) =>
          service.dponet.trainingEnrollment.destroy({ courseId, enrollmentId }),
        ),
      )
      setSelected([])
      setMassDeleteOpen(false)
      refresh()
      snackbar.open({
        message: 'Alunos removidos com sucesso.',
        variant: 'success',
      })
    } catch {
      snackbar.open({ message: 'Erro ao remover alunos.', variant: 'error' })
    } finally {
      setMassDeleteLoading(false)
    }
  }

  return (
    <Box>
      <LoadingFeedback open={isLoading} />
      <Box mb={2}>
        <PublicLinkBanner courseId={courseId} />
      </Box>
      {isEdit && (
        <>
          <Box className={classes.toolbar}>
            <Button
              variant="contained"
              color="primary"
              startIcon={<PersonAddIcon />}
              onClick={() => setLinkOpen(true)}
            >
              Gerenciar Alunos
            </Button>
            <Button
              variant="outlined"
              startIcon={<DeleteIcon />}
              disabled={isEmpty(selected)}
              onClick={() => setMassDeleteOpen(true)}
            >
              Excluir
            </Button>
            <Button
              variant="outlined"
              startIcon={<NotifyIcon />}
              disabled={isEmpty(selected)}
              onClick={() => setMassNotifyOpen(true)}
            >
              Notificar
            </Button>
            <FilterButton setDrawerOpen={filter.setDrawerOpen} />
          </Box>
        </>
      )}

      <Card>
        <Table
          size="small"
          emptyMessage="Nenhum dado encontrado"
          noWrap
          isLoading={isLoading}
        >
          <TableHead>
            <TableRow>
              {isEdit && (
                <TableCell padding="checkbox" className={classes.checkboxCell}>
                  <Checkbox
                    indeterminate={someSelected}
                    checked={allSelected}
                    onChange={toggleSelectAll}
                  />
                </TableCell>
              )}
              <TableCell>ID Usuário</TableCell>
              <TableCell>ID Matricula</TableCell>
              <TableCell>Nome</TableCell>
              <TableCell>E-mail</TableCell>
              <TableCell>Telefone</TableCell>
              <TableCell>Prazo</TableCell>
              <TableCell>Notificar Em</TableCell>
              <TableCell>Última Notificação</TableCell>
              <TableCell>Status</TableCell>
              {isEdit && <TableCell>Ações</TableCell>}
              <TableCell className={classes.actionsCell} />
            </TableRow>
          </TableHead>
          <TableBody>
            {enrollments?.map((enrollment) => (
              <TableRow key={enrollment?.id} hover>
                {isEdit && (
                  <TableCell padding="checkbox">
                    <Checkbox
                      checked={selected.includes(enrollment.id)}
                      onChange={() => toggleSelect(enrollment.id)}
                    />
                  </TableCell>
                )}
                <TableCell>#{enrollment?.user?.id}</TableCell>
                <TableCell>#{enrollment?.id}</TableCell>
                <TableCell>{enrollment?.user?.name ?? '-'}</TableCell>
                <TableCell>{enrollment?.user?.email ?? '-'}</TableCell>
                <TableCell>{enrollment?.user?.phone ?? '-'}</TableCell>
                <TableCell>
                  {formatteDate(enrollment?.deadline) ?? '-'}
                </TableCell>
                <TableCell>
                  {formatteDate(enrollment?.notificationDate) ?? '-'}
                </TableCell>
                <TableCell>
                  {formatteDate(enrollment?.sentAt, true) ?? '-'}
                </TableCell>
                <TableCell>
                  <Chip
                    label={
                      STATUS_LABELS[enrollment.status] ?? enrollment.status
                    }
                    color={STATUS_COLORS[enrollment.status] ?? 'default'}
                    size="small"
                    className={classes.statusChip}
                  />
                </TableCell>
                {isEdit && (
                  <TableCell className={classes.actionsCell}>
                    <RowMenu
                      enrollment={enrollment}
                      onEdit={setEditEnrollment}
                      onInactivate={setInactivateEnrollment}
                      onDelete={setDeleteEnrollment}
                      onNotify={setNotifyEnrollment}
                    />
                  </TableCell>
                )}
              </TableRow>
            ))}
          </TableBody>
        </Table>
        <TablePagination
          component="div"
          count={totalCount}
          page={page - 1}
          onPageChange={handleChangePage}
          rowsPerPage={perPage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          rowsPerPageOptions={[10, 25, 50]}
          labelRowsPerPage="Por página"
          labelDisplayedRows={({ from, to, count }) =>
            `${from}–${to} de ${count}`
          }
        />
      </Card>
      {massDeleteOpen && (
        <ConfirmMassDeleteDialog
          open={massDeleteOpen}
          count={selected.length}
          loading={massDeleteLoading}
          onClose={() => setMassDeleteOpen(false)}
          onConfirm={handleMassDelete}
        />
      )}
      {linkOpen && (
        <LinkStudentDialog
          open={linkOpen}
          courseId={courseId}
          onClose={() => setLinkOpen(false)}
          onSuccess={handleLinkSuccess}
        />
      )}
      {editEnrollment && (
        <EnrollmentFormDialog
          open={Boolean(editEnrollment)}
          enrollment={editEnrollment}
          courseId={courseId}
          onClose={() => setEditEnrollment(null)}
          onSuccess={handleEditSuccess}
        />
      )}
      {deleteEnrollment && (
        <ConfirmDeleteDialog
          open={Boolean(deleteEnrollment)}
          enrollment={deleteEnrollment}
          courseId={courseId}
          onClose={() => setDeleteEnrollment(null)}
          onSuccess={handleDeleteSuccess}
        />
      )}
      {inactivateEnrollment && (
        <ConfirmInactivateDialog
          open={Boolean(inactivateEnrollment)}
          enrollment={inactivateEnrollment}
          courseId={courseId}
          onClose={() => setInactivateEnrollment(null)}
          onSuccess={handleInactivateSuccess}
        />
      )}
      {(notifyEnrollment || massNotifyOpen) && (
        <NotifyDialog
          open={Boolean(notifyEnrollment) || massNotifyOpen}
          courseId={courseId}
          enrollmentIds={
            massNotifyOpen
              ? selected
              : notifyEnrollment
              ? [notifyEnrollment?.id]
              : []
          }
          onClose={() => {
            setNotifyEnrollment(null)
            setMassNotifyOpen(false)
          }}
          onSuccess={handleNotifySuccess}
        />
      )}
      <Filters filter={filter}>
        <input textfieldinput="true" label="Identificador" name="userId" />
        <input textfieldinput="true" label="E-mail" name="userEmail" />
        <input textfieldinput="true" label="Telefone" name="userPhone" />
        <select textfieldinput="true" label="Status" name="status">
          <option value="" />
          <option value="active">Ativo</option>
          <option value="inactive">Inativo</option>
        </select>
        <input datepickerinput="true" label="Prazo" name="deadline" />
        <input
          datepickerinput="true"
          label="Notificar em"
          name="notificationDate"
        />
        <input
          datepickerinput="true"
          label="Última notificação"
          name="sentAt"
        />
        <select textfieldinput="true" label="Notificado" name="notified">
          <option value="" />
          <option value="true">Notificado</option>
          <option value="false">Não notificado</option>
        </select>
      </Filters>
    </Box>
  )
}

export default StepManagePeople
